import React from "react";
import { CommonHelper } from "../../../../../../../../react-core/Helpers/CommonHelper";
import { AddressDisplay } from "../../../../../../../../react-core/Html/Address/AddressDisplay";
import { FormComponent } from "../../../../../../../../react-core/Html/FormComponent";
import { PhysicalAddressType } from "../../../../../../../../Wrap/Constants/Enums/Customer/PhysicalAddressType";
import { RoleType } from "../../../../../../../../Wrap/Constants/Enums/RoleType";
import { CorrespondenceRecipient } from "../../../../../../../../Wrap/Models/Domains/Account/CorrespondenceRecipient";
import { AddressDetailModel } from "../../../../../../../../Wrap/Models/Models/Customer/AddressDetailModel";
import { OrganisationMember } from "../../../../../../../../Wrap/Models/Models/Customer/OrganisationMember";
import { Lightbox, Lightbox as LightBoxContainer } from "../../../../../../../../react-core/Html/Components/Lightbox";
import { Alert } from "../../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../../Shared/Entities/Constants/AlertConstants";
import { CheckboxEditor } from "../../../../../../../../react-core/Html/CheckboxEditor";
import { RelationshipType } from "../../../../../../../../Wrap/Constants/Enums/Customer/RelationshipType";
import { AccountRoleActorCategory } from "../../../../../../../../Wrap/Constants/Enums/AccountRoleActorCategory";

interface ICorrespondenceLightboxProps {
    data: Array<OrganisationMember>;
    headAccountHierarchyId: string;
    correspondenceRecipient: CorrespondenceRecipient;
    onClose: () => void;
    onCorrespondenceRecipientChange: (val: CorrespondenceRecipient) => void;
}

interface ICorrespondenceLightboxState {
    isEdit: boolean;
    showCancel: boolean;
    showLightbox: boolean;
    updatedRecipient: CorrespondenceRecipient;
    showErrorMsg: boolean;
}

export class CorrespondenceLightbox extends FormComponent<ICorrespondenceLightboxProps, ICorrespondenceLightboxState> {
    constructor(props: ICorrespondenceLightboxProps) {
        super(props);
        this.state = {
            isEdit: false,
            showCancel: false,
            showLightbox: true,
            updatedRecipient: null,
            showErrorMsg: false,
        };
    }

    componentDidMount() {
        this.setState({ updatedRecipient: this.props.correspondenceRecipient });
    }

    private renderOptions(): JSX.Element[] {
        const { data, correspondenceRecipient } = this.props;
        const { isEdit, updatedRecipient } = this.state;
        const members = JSON.parse(JSON.stringify(data));
        if (correspondenceRecipient && correspondenceRecipient?.ActorId) {
            members.unshift(
                members.splice(
                    members.indexOf(members.filter((x) => x.CustomerId == correspondenceRecipient.ActorId)[0]),
                    1
                )[0]
            );
        }
        const options = [];
        members.forEach((member: OrganisationMember) => {
            if (member.Company != null) {
                options.push(
                    <>
                        <h4>{member?.Company?.CompanyName}</h4>
                        <CheckboxEditor
                            label="Receives correspondence"
                            value={updatedRecipient?.ActorId == member.CustomerId}
                            onChange={(v) => {
                                if (v) {
                                    this.setState({
                                        showErrorMsg: false,
                                        updatedRecipient: {
                                            HeadAccountHierarchyId: this.props.headAccountHierarchyId,
                                            ActorId: member.CustomerId,
                                            RoleType: this.getAccountRoleType(member),
                                        },
                                    });
                                } else {
                                    this.setState({ updatedRecipient: null });
                                }
                            }}
                            disabled={!isEdit}
                        />
                        {this.renderAddress(member?.Company?.AddressDetails)}
                        <hr />
                    </>
                );
            } else {
                options.push(
                    <>
                        <h4>
                            {member?.Individual?.PersonalDetails?.GivenName +
                                " " +
                                member?.Individual?.PersonalDetails?.FamilyName}
                        </h4>

                        <CheckboxEditor
                            label="Receives correspondence"
                            value={updatedRecipient?.ActorId == member.CustomerId}
                            onChange={(v) => {
                                if (v) {
                                    this.setState({
                                        showErrorMsg: false,
                                        updatedRecipient: {
                                            HeadAccountHierarchyId: this.props.headAccountHierarchyId,
                                            ActorId: member.CustomerId,
                                            RoleType: this.getAccountRoleType(member),
                                        },
                                    });
                                } else {
                                    this.setState({ updatedRecipient: null });
                                }
                            }}
                            disabled={!isEdit}
                        />
                        {this.renderAddress(member?.Individual?.AddressDetails)}
                        <hr />
                    </>
                );
            }
        });
        return options;
    }

    private getAccountRoleType(member: OrganisationMember) {
        if (
            member?.Relationships.includes(RelationshipType.IndividualTrustee) ||
            member?.Relationships.includes(RelationshipType.CorporateTrustee)
        ) {
            return RoleType.Trustee;
        } else if (member?.Relationships.includes(RelationshipType.Settlor)) {
            return RoleType.Settlor;
        } else if (member?.Relationships.includes(RelationshipType.Beneficiary)) {
            if (member?.Individual?.ActorCategory == AccountRoleActorCategory.Customer) {
                return RoleType.Beneficiary;
            }
            return RoleType.AdditionalBeneficiary;
        } else {
            return RoleType.Trustee;
        }
    }

    private renderAddress(addressDetails: AddressDetailModel[]) {
        const postalAddress = addressDetails.filter((x) => x.PhysicalAddressType == PhysicalAddressType.Postal)[0];
        if (postalAddress) {
            return <AddressDisplay address={postalAddress} />;
        } else if (!CommonHelper.isNullOrEmpty(addressDetails)) {
            return <AddressDisplay address={addressDetails[0]} />;
        }
        return <span>No address record found</span>;
    }

    render() {
        const { isEdit, showCancel, updatedRecipient, showLightbox, showErrorMsg } = this.state;
        return (
            <>
                {showLightbox && (
                    <LightBoxContainer
                        cssWrapper="is-default-lightbox is-narrow"
                        noOverlay={false}
                        noAutofocus={true}
                        onClose={() => {
                            if (isEdit) {
                                this.setState({ showCancel: true });
                            } else {
                                this.setState({ showLightbox: false });
                                this.props.onClose();
                            }
                        }}
                        show={true}
                        title={"Receives correspondence"}
                    >
                        {showErrorMsg && (
                            <Alert
                                alertType={AlertType.Error}
                                message={"Please select a correspondence recipient to continue."}
                            />
                        )}

                        {this.renderOptions()}
                        {!isEdit && (
                            <button
                                className="btn primary mb-4"
                                onClick={() => {
                                    this.setState({ isEdit: true });
                                }}
                            >
                                Edit
                            </button>
                        )}
                        {isEdit && (
                            <>
                                <div className="row">
                                    <button
                                        className="btn secondary mr-12"
                                        onClick={() => {
                                            if (isEdit) {
                                                this.setState({ showCancel: true });
                                            }
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn primary"
                                        onClick={() => {
                                            if (updatedRecipient == null) {
                                                this.setState({ showErrorMsg: true });
                                            } else {
                                                this.props.onCorrespondenceRecipientChange(updatedRecipient);
                                                this.setState({ isEdit: false });
                                            }
                                        }}
                                    >
                                        Save
                                    </button>
                                </div>
                            </>
                        )}
                    </LightBoxContainer>
                )}

                {showCancel && (
                    <Lightbox
                        noOverlay={false}
                        noAutofocus={true}
                        show={showCancel}
                        title={"Cancel"}
                        unclosable={true}
                        onClose={() => {
                            this.setState({ showCancel: false });
                        }}
                        cssWrapper="is-default-lightbox is-narrow"
                    >
                        <p className="mb-10">Are you sure you wish to cancel?</p>
                        <button
                            className="btn primary  mt-20 mr-20"
                            onClick={() => {
                                this.setState({
                                    showCancel: false,
                                    isEdit: false,
                                    updatedRecipient: this.props.correspondenceRecipient,
                                });
                            }}
                        >
                            Yes
                        </button>
                        <button
                            className="btn secondary  mt-20"
                            onClick={() => {
                                this.setState({ showCancel: false });
                            }}
                        >
                            No
                        </button>
                    </Lightbox>
                )}
            </>
        );
    }
}
